import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Typography, alpha, Tooltip, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";

// ── Icons (more distinctive per feature) ──────────────────────────────────────
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import PieChartOutlinedIcon from "@mui/icons-material/PieChartOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import ManageAccountsOutlinedIcon from "@mui/icons-material/ManageAccountsOutlined";
import HexagonOutlinedIcon from "@mui/icons-material/HexagonOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import RouterOutlinedIcon from "@mui/icons-material/RouterOutlined";
import DiamondOutlinedIcon from "@mui/icons-material/DiamondOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";

export default function HeaderDrawer({
  navbarColor,
  setDrawerStatus,
  brokerMarkets,
  launchIco,
  launchNft,
  launchHcNet,
  launchBrokers,
}) {
  const navigate = useNavigate();
  const { btncolor } = useSelector((state) => state);
  const accent = btncolor || "#1976d2";

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchApps = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      setLoading(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          authorization: `BEARER ${token}`,
        };
        const response = await axios.post(
          "https://accounts.paybito.com/api/app/getAllInstalledApp",
          {
            userUuid: localStorage.getItem("uuid") || "",
          },
          { headers }
        );
        if (response?.data?.appLists) {
          setApps(response.data.appLists);
        }
      } catch (error) {
        console.error("Failed to fetch installed apps:", error);
      } finally {
        setLoading(false);
      }
    };


    fetchApps();
  }, []);

  const handleClick = (url) => {
    navigate(url);
    setDrawerStatus(false);
  };

  // ── Grid items definition ─────────────────────────────────────────────────
  const items = [];

  if (brokerMarkets?.isBank === 1) {
    items.push({
      key: "earn",
      icon: <TrendingUpOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "Earn",
      desc: "Make your cryptos work for you",
      tourClass: "tour22",
      action: () => handleClick("/earn-interest"),
      color: "#16a34a",
    });
    items.push({
      key: "loans",
      icon: <AccountBalanceOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "Loans",
      desc: "Get instant loans using your crypto assets",
      tourClass: "tour23",
      action: () => handleClick("/crypto-loans"),
      color: "#0ea5e9",
    });
    items.push({
      key: "portfolio",
      icon: <PieChartOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "Portfolio",
      desc: "Create your investment portfolio",
      tourClass: "tour24",
      action: () => handleClick("/portfolio-invest"),
      color: "#8b5cf6",
    });
  }

  if (brokerMarkets?.isIco === 1) {
    items.push({
      key: "ico",
      icon: <RocketLaunchOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "Launch ICO",
      desc: "Sign up and launch your own ICO today",
      tourClass: "tour25",
      action: launchIco,
      color: "#f59e0b",
    });
  }

  if (brokerMarkets?.isAssetMgmt === 1) {
    items.push({
      key: "assetmgmt",
      icon: <ManageAccountsOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "Asset Mgmt",
      desc: "Create sub account and delegate trading activities",
      tourClass: "tour26",
      action: () => handleClick("/asset-management"),
      color: "#ec4899",
    });
  }

  if (brokerMarkets?.isWeb3Wallet) {
    items.push({
      key: "web3",
      icon: <HexagonOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "Web3 Wallet",
      desc: "Secure decentralized wallets for your assets",
      tourClass: "tour27",
      action: () => handleClick("/web3-wallet"),
      color: "#06b6d4",
    });
  }

  // if (localStorage.getItem("BROKERID") === "PAYB18022021121103") {
  //   items.push({
  //     key: "brokers",
  //     icon: <StorefrontOutlinedIcon sx={{ fontSize: 22 }} />,
  //     label: "Brokers",
  //     desc: "White-label exchange solution",
  //     tourClass: "tourBrokers",
  //     action: launchBrokers,
  //     color: "#64748b",
  //   });
  // }

  if (brokerMarkets?.isHcNet === 1) {
    items.push({
      key: "hcnet",
      icon: <RouterOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "HC NET",
      desc: "Build your own DApps",
      tourClass: "tour28",
      action: launchHcNet,
      color: "#10b981",
    });
  }

  if (brokerMarkets?.isMiningPool === 1) {
    items.push({
      key: "mining",
      icon: <MemoryOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "Mining Pool",
      desc: "Get more rewards by connecting to the pool",
      tourClass: "tour29",
      action: () => handleClick("/mining-pool"),
      color: "#f97316",
    });
  }

  if (brokerMarkets?.isNft === 1) {
    items.push({
      key: "nft",
      icon: <DiamondOutlinedIcon sx={{ fontSize: 22 }} />,
      label: "NFT",
      desc: "Find the most useful NFTs here",
      tourClass: "tour30",
      action: launchNft,
      color: "#a855f7",
    });
  }

  return (
    <Box sx={drawerWrap}>
      {/* ── Header ── */}
      <Box sx={drawerHeader}>
        <Box
          sx={{
            ...drawerHeaderAccent,
            background: `linear-gradient(180deg, ${accent} 0%, ${alpha(
              accent,
              0.3
            )} 100%)`,
          }}
        />
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <Typography sx={drawerHeaderTitle}>Explore More</Typography>
          <Typography sx={drawerHeaderSub}>All platform features</Typography>
        </Box>
      </Box>

      {/* ── Grid ── */}
      <Box sx={gridWrap}>
        {items.map((item) => (
          <Tooltip key={item.key} title={item.desc} placement="top" arrow>
            <Box className={item.tourClass} onClick={item.action} sx={gridItem}>
              {/* Icon circle */}
              <Box
                sx={{
                  ...iconCircle,
                  backgroundColor: alpha(item.color, 0.12),
                  border: `1.5px solid ${alpha(item.color, 0.28)}`,
                }}
              >
                <Box sx={{ color: item.color, display: "flex" }}>
                  {item.icon}
                </Box>
              </Box>

              {/* Label */}
              <Typography sx={itemLabel}>{item.label}</Typography>
              <Typography sx={itemDesc}>{item.desc}</Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>

      {/* ── Loading Spinner ── */}
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 3, borderTop: "1px solid rgba(125,125,125,0.12)" }}>
          <CircularProgress size={24} sx={{ color: accent }} />
        </Box>
      )}

      {/* ── Apps Section Header ── */}
      {!loading && apps.length > 0 && (
        <Box sx={sectionHeader}>
          <Box
            sx={{
              ...drawerHeaderAccent,
              background: `linear-gradient(180deg, ${accent} 0%, ${alpha(
                accent,
                0.3
              )} 100%)`,
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={drawerHeaderTitle}>My Apps</Typography>
            <Typography sx={drawerHeaderSub}>Your installed applications</Typography>
          </Box>
        </Box>
      )}

      {/* ── Apps Grid ── */}
      {!loading && apps.length > 0 && (
        <Box sx={gridWrap}>
          {apps.map((app) => (
            <Tooltip key={app.appId} title={app.description || ""} placement="top" arrow>
              <Box
                onClick={() => {
                  if (app.appUrl) {
                    window.open(app.appUrl.trim(), "_blank");
                  }
                  setDrawerStatus(false);
                }}
                sx={gridItem}
              >
                {/* Icon image */}
                <Box
                  sx={{
                    ...iconCircle,
                    backgroundColor: "transparent",
                    border: "none",
                  }}
                >
                  <img
                    src={app.icon}
                    alt={app.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "10px",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://via.placeholder.com/42?text=" + (app.name ? app.name[0] : "A");
                    }}
                  />
                </Box>

                {/* Label */}
                <Typography sx={itemLabel}>{app.name}</Typography>
                <Typography sx={itemDesc}>{app.description}</Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const drawerWrap = {
  width: 380, // Reduced from 420
  backgroundColor: "var(--popupbody-color, var(--navbar-color-less))",
  border: "1px solid rgba(125,125,125,0.15)",
  borderRadius: "16px",
  boxShadow: "0 16px 48px rgba(0,0,0,0.32)",
  overflow: "hidden",
  position: "absolute",
  left: "-190px", // Centered (380/2)
  top: "44px",
  zIndex: 9,
  maxHeight: "80vh",
  overflowY: "auto",
};

const drawerHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  px: 2,
  py: 1.5,
  backgroundColor: "var(--popupheader-color, var(--navbar-color-less))",
  borderBottom: "1px solid rgba(125,125,125,0.12)",
};

const drawerHeaderAccent = {
  width: 3,
  borderRadius: "3px",
  alignSelf: "stretch",
  minHeight: 28,
  flexShrink: 0,
};

const drawerHeaderTitle = {
  fontSize: "0.88rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
};

const drawerHeaderSub = {
  fontSize: "0.68rem",
  color: "var(--text-color)",
  opacity: 0.45,
  mt: 0.15,
};

const gridWrap = {
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "1px",
  backgroundColor: "var(--popupbody-color, var(--navbar-color-less))",
  "& > *:last-child:nth-of-type(3n-1)": {
    borderRight: "none",
  },
};

const gridItem = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start", // Changed from center to align text and icons to the left
  justifyContent: "flex-start",
  gap: 0.75,
  py: 2,
  px: 2, // Increased horizontal padding
  cursor: "pointer",
  backgroundColor: "var(--popupbody-color, var(--navbar-color-less))",
  transition: "background-color 0.15s ease, transform 0.15s ease",
  "&:hover": {
    backgroundColor: "var(--navbar-color-less2, rgba(125,125,125,0.08))",
    "& .MuiBox-root": {
      transform: "scale(1.05)",
    },
  },
};

const iconCircle = {
  width: 42,
  height: 42,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "transform 0.15s ease",
  flexShrink: 0,
  mb: 0.5,
};

const itemLabel = {
  fontSize: "0.75rem",
  fontWeight: 700,
  color: "var(--text-color)",
  lineHeight: 1.2,
  textAlign: "left", // Left align label
  letterSpacing: "0.01em",
  width: "100%",
};

const itemDesc = {
  fontSize: "0.62rem",
  color: "var(--text-color)",
  opacity: 0.45,
  lineHeight: 1.3,
  textAlign: "left", // Left align description
  width: "100%",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const sectionHeader = {
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  px: 2,
  py: 1.5,
  backgroundColor: "var(--popupheader-color, var(--navbar-color-less))",
  borderTop: "1px solid rgba(125,125,125,0.12)",
  borderBottom: "1px solid rgba(125,125,125,0.12)",
};
